import React, { useEffect, useState } from "react";
import axios from "axios";
import "./VideoPlayer.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

/* =========================
   PLAYLIST CONFIG
========================= */

const PLAYLISTS = [
  {
    id: "PLth5BbxWzguNojWBU42nhJ01pRqU9ZpQT",
    name: "Prison Addon",
    description: "Prison systems, mines, updates & gameplay guides.",
    icon: "fa-solid fa-gavel",
  },
  {
    id: "PLB8Os_ipW4VE",
    name: "SkyBlock Addon",
    description: "SkyBlock islands, farming systems & tutorials.",
    icon: "fa-solid fa-cloud",
  },
];

const API_KEY = process.env.REACT_APP_API_KEY_YT_DATA;

export default function VideoPlayer() {
  const [selectedPlaylist, setSelectedPlaylist] = useState(PLAYLISTS[0]);
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);

  /* =========================
     LOAD YOUTUBE PLAYLIST
  ========================= */

  useEffect(() => {
    async function fetchVideos() {
      setLoading(true);
      setError(null);

      try {
        if (!API_KEY) {
          throw new Error("Missing YouTube API key (.env)");
        }

        const res = await axios.get(
          "https://www.googleapis.com/youtube/v3/playlistItems",
          {
            params: {
              part: "snippet,contentDetails",
              playlistId: selectedPlaylist.id,
              maxResults: 25,
              key: API_KEY,
            },
          }
        );

        if (!res.data.items) {
          throw new Error("No videos found in playlist");
        }

        const formatted = res.data.items.map((item) => ({
          id: item.contentDetails.videoId,
          title: item.snippet.title,
          thumbnail: item.snippet.thumbnails?.medium?.url,
          channel:
            item.snippet.videoOwnerChannelTitle || "VortexNetwork Studios",
        }));

        setVideos(formatted);
        setCurrentVideo(formatted[0] || null);
        setPlaying(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load videos. Check API key or playlist ID.");
      } finally {
        setLoading(false);
      }
    }

    fetchVideos();
  }, [selectedPlaylist]);

  /* =========================
     LOADING STATE
  ========================= */

  if (loading) {
    return (
      <div className="vp-loading">
        <div className="vp-spinner"></div>
        <p>Loading VortexNetwork Videos...</p>
      </div>
    );
  }

  /* =========================
     ERROR STATE
  ========================= */

  if (error) {
    return (
      <div className="vp-error">
        <h2>⚠️ {error}</h2>
        <p>Check your API key or YouTube playlist settings.</p>
      </div>
    );
  }

  /* =========================
     HERO RENDER START
  ========================= */

  return (
    <div className="vp-page">

      {/* Background Glow */}
      <div className="vp-glow vp-glow1"></div>
      <div className="vp-glow vp-glow2"></div>

      {/* HERO SECTION */}
      <section className="vp-hero">

        <div className="vp-badge">
          <i className="fa-brands fa-youtube"></i>
          VortexNetwork Studios Videos
        </div>

        <h1>
          Marketplace Addons &<br />
          <span>Gameplay Content</span>
        </h1>

        <p>
          Watch tutorials, updates, Minecraft Marketplace addons and behind the scenes development for
          all VortexNetwork Studios.
        </p>

      </section>

      {/* MAIN LAYOUT STARTS IN PART 2 */}
        {/* =========================
            MAIN CONTAINER
        ========================= */}

        <div className="vp-container">

          {/* =========================
              LEFT - FEATURED PLAYER
          ========================= */}

          <div className="vp-main">

            {/* FEATURED VIDEO TITLE */}
            <div className="vp-title-block">

              <h2 className="vp-video-title">
                {currentVideo?.title || "Loading video..."}
              </h2>

              <p className="vp-video-desc">
                {selectedPlaylist.description}
              </p>

            </div>

            {/* VIDEO PLAYER */}
            <div
              className="vp-video-wrapper"
              onClick={() => setPlaying(true)}
            >

              {/* THUMBNAIL VIEW */}
              {!playing && currentVideo && (
                <div className="vp-overlay">

                  <img
                    src={`https://img.youtube.com/vi/${currentVideo.id}/maxresdefault.jpg`}
                    alt={currentVideo.title}
                    className="vp-thumbnail"
                  />

                  <div className="vp-play-button">
                    <i className="fa-solid fa-play"></i>
                  </div>

                </div>
              )}

              {/* YOUTUBE IFRAME */}
              {playing && currentVideo && (
                <iframe
                  title={currentVideo.title}
                  src={`https://www.youtube.com/embed/${currentVideo.id}?autoplay=1`}
                  allowFullScreen
                />
              )}

            </div>

            {/* =========================
                VIDEO STATS
            ========================= */}

            <div className="vp-stats">

              <div className="vp-stat">
                <i className="fa-solid fa-film"></i>
                <div>
                  <h3>{videos.length}</h3>
                  <p>Videos</p>
                </div>
              </div>

              <div className="vp-stat">
                <i className="fa-solid fa-cube"></i>
                <div>
                  <h3>Minecraft</h3>
                  <p>Marketplace Content</p>
                </div>
              </div>

              <div className="vp-stat">
                <i className="fa-solid fa-bolt"></i>
                <div>
                  <h3>Weekly</h3>
                  <p>Updates</p>
                </div>
              </div>

            </div>

          </div>

          {/* =========================
              RIGHT - PLAYLIST SIDEBAR
          ========================= */}

          <aside className="vp-sidebar">

            {/* PLAYLIST SELECTOR */}
            <div className="vp-playlist-selector">

              {PLAYLISTS.map((p) => (
                <button
                  key={p.id}
                  className={`vp-playlist-btn ${
                    selectedPlaylist.id === p.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedPlaylist(p);
                    setPlaying(false);
                  }}
                >

                  <i className={p.icon}></i>

                  <div>
                    <h4>{p.name}</h4>
                    <span>{p.description}</span>
                  </div>

                </button>
              ))}

            </div>

            {/* VIDEO LIST */}
            <div className="vp-video-list">

              {videos.map((video, index) => (
                <div
                  key={video.id}
                  className={`vp-video-item ${
                    currentVideo?.id === video.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setCurrentVideo(video);
                    setPlaying(false);
                  }}
                >

                  <div className="vp-thumb">

                    <img
                      src={video.thumbnail}
                      alt={video.title}
                    />

                    <div className="vp-index">
                      {index + 1}
                    </div>

                  </div>

                  <div className="vp-info">
                    <h4>{video.title}</h4>
                    <p>{video.channel}</p>
                  </div>

                </div>
              ))}

            </div>

          </aside>

        </div>

        {/* =========================
            FOOTER END NOTE
        ========================= */}

        <footer className="vp-footer">
          <p>
            VortexNetwork Studios • Minecraft Marketplace Addons • Videos & Updates
          </p>
        </footer>

      </div>
  );
}      